import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Plus, Trophy, Target, CheckCircle, Clock, Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL, API } from "@/lib/api";


export default function TeamMissions({ eventId, isOwner }) {
  const [teams, setTeams] = useState([]);
  const [missions, setMissions] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState(localStorage.getItem("team_mission_team") || "");
  
  // Team creation
  const [newTeamName, setNewTeamName] = useState("");
  const [newTeamMembers, setNewTeamMembers] = useState("");
  const [autoTeamCount, setAutoTeamCount] = useState(3);
  
  // Mission creation
  const [newMission, setNewMission] = useState({
    title: "",
    description: "",
    mission_type: "task",
    points: 10,
    deadline: "",
    quiz_question: "",
    quiz_options: ["", ""],
    quiz_correct: ""
  });

  useEffect(() => {
    fetchTeams();
    fetchMissions();
    fetchLeaderboard();
  }, [eventId]);

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/teams`);
      setTeams(response.data);
    } catch (error) {
      console.error("Failed to fetch teams:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMissions = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/missions`);
      setMissions(response.data);
    } catch (error) {
      console.error("Failed to fetch missions:", error);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/teams/leaderboard`);
      setLeaderboard(response.data);
    } catch (error) {
      console.error("Failed to fetch leaderboard:", error);
    }
  };

  const handleCreateTeam = async () => {
    if (!newTeamName.trim() || !newTeamMembers.trim()) {
      toast.error("Name und Mitglieder erforderlich!");
      return;
    }

    const members = newTeamMembers.split(",").map(m => m.trim()).filter(m => m);

    try {
      await axios.post(
        `${API}/events/${eventId}/teams`,
        { name: newTeamName, members },
        { withCredentials: true }
      );
      toast.success("Team erstellt!");
      setNewTeamName("");
      setNewTeamMembers("");
      fetchTeams();
      fetchLeaderboard();
    } catch (error) {
      toast.error("Fehler beim Erstellen");
    }
  };

  const handleAutoGenerateTeams = async () => {
    try {
      await axios.post(
        `${API}/events/${eventId}/teams/auto-generate?num_teams=${autoTeamCount}`,
        {},
        { withCredentials: true }
      );
      toast.success(`${autoTeamCount} Teams automatisch erstellt!`);
      fetchTeams();
      fetchLeaderboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Generieren");
    }
  };

  const handleCreateMission = async () => {
    if (!newMission.title.trim() || !newMission.description.trim()) {
      toast.error("Titel und Beschreibung erforderlich!");
      return;
    }

    try {
      await axios.post(
        `${API}/events/${eventId}/missions`,
        newMission,
        { withCredentials: true }
      );
      toast.success("Mission erstellt!");
      setNewMission({
        title: "",
        description: "",
        mission_type: "task",
        points: 10,
        deadline: "",
        quiz_question: "",
        quiz_options: ["", ""],
        quiz_correct: ""
      });
      fetchMissions();
    } catch (error) {
      toast.error("Fehler beim Erstellen");
    }
  };

  const handleSubmitMission = async (missionId, missionType) => {
    if (!selectedTeam) {
      toast.error("Bitte wähle dein Team!");
      return;
    }

    const submissionData = {
      team_id: selectedTeam
    };

    if (missionType === "task" || missionType === "checkin") {
      submissionData.submission_text = "Erledigt";
    }

    try {
      await axios.post(
        `${API}/events/${eventId}/missions/${missionId}/submit`,
        submissionData
      );
      toast.success("Mission eingereicht! ✅");
      localStorage.setItem("team_mission_team", selectedTeam);
      fetchMissions();
      fetchLeaderboard();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error("Bereits eingereicht!");
      } else {
        toast.error("Fehler beim Einreichen");
      }
    }
  };

  const handleSubmitPhoto = async (missionId, file) => {
    if (!selectedTeam || !file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      toast.loading("Foto wird hochgeladen...", { id: "photo-upload" });
      await axios.post(
        `${API}/events/${eventId}/missions/${missionId}/submit-photo?team_id=${selectedTeam}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success("Foto eingereicht! Warte auf Freigabe 📸", { id: "photo-upload" });
      fetchMissions();
    } catch (error) {
      toast.error("Fehler beim Hochladen", { id: "photo-upload" });
    }
  };

  const handleApproveSubmission = async (missionId, teamId) => {
    try {
      await axios.patch(
        `${API}/events/${eventId}/missions/${missionId}/submissions/${teamId}/approve`,
        {},
        { withCredentials: true }
      );
      toast.success("Submission freigegeben!");
      fetchMissions();
      fetchLeaderboard();
    } catch (error) {
      toast.error("Fehler beim Freigeben");
    }
  };

  const getMissionIcon = (type) => {
    switch (type) {
      case "task": return "✅";
      case "photo": return "📸";
      case "checkin": return "📍";
      case "quiz": return "❓";
      case "challenge": return "🎯";
      default: return "🎯";
    }
  };

  const getMissionStatus = (mission, teamId) => {
    const submission = mission.submissions?.find(s => s.team_id === teamId);
    if (!submission) return { status: "todo", label: "Zu erledigen", color: "bg-secondary" };
    if (submission.status === "approved") return { status: "done", label: "Erledigt", color: "bg-green-500" };
    if (submission.status === "pending") return { status: "pending", label: "Ausstehend", color: "bg-yellow-500" };
    if (submission.status === "rejected") return { status: "rejected", label: "Abgelehnt", color: "bg-red-500" };
    return { status: "todo", label: "Zu erledigen", color: "bg-secondary" };
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold flex items-center gap-2 mb-2">
          <Target className="w-5 h-5 text-primary" />
          Team-Missionen
        </h3>
        <p className="text-sm text-muted-foreground">
          Teamwork macht den Traum! Missionen gemeinsam meistern 🎯
        </p>
      </div>

      {isOwner ? (
        // Owner View
        <Tabs defaultValue="missions" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="missions">Missionen</TabsTrigger>
            <TabsTrigger value="teams">Teams</TabsTrigger>
            <TabsTrigger value="leaderboard">Leaderboard</TabsTrigger>
          </TabsList>

          {/* Missions Tab */}
          <TabsContent value="missions" className="space-y-4">
            <div className="p-4 bg-secondary/20 rounded-lg space-y-3">
              <h4 className="font-medium">🎯 Neue Mission erstellen</h4>
              
              <div>
                <Label>Titel</Label>
                <Input
                  value={newMission.title}
                  onChange={(e) => setNewMission({ ...newMission, title: e.target.value })}
                  placeholder="z.B. Team-Foto machen"
                />
              </div>

              <div>
                <Label>Beschreibung</Label>
                <Textarea
                  value={newMission.description}
                  onChange={(e) => setNewMission({ ...newMission, description: e.target.value })}
                  placeholder="Detaillierte Beschreibung der Mission..."
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Mission-Typ</Label>
                  <Select
                    value={newMission.mission_type}
                    onValueChange={(val) => setNewMission({ ...newMission, mission_type: val })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="task">✅ Aufgabe</SelectItem>
                      <SelectItem value="photo">📸 Foto-Mission</SelectItem>
                      <SelectItem value="checkin">📍 Check-in</SelectItem>
                      <SelectItem value="quiz">❓ Quiz</SelectItem>
                      <SelectItem value="challenge">🎯 Challenge</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Punkte</Label>
                  <Input
                    type="number"
                    value={newMission.points}
                    onChange={(e) => setNewMission({ ...newMission, points: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>

              {newMission.mission_type === "quiz" && (
                <div className="space-y-2">
                  <Label>Quiz-Frage</Label>
                  <Input
                    value={newMission.quiz_question}
                    onChange={(e) => setNewMission({ ...newMission, quiz_question: e.target.value })}
                    placeholder="Frage eingeben..."
                  />
                  <Label>Richtige Antwort</Label>
                  <Input
                    value={newMission.quiz_correct}
                    onChange={(e) => setNewMission({ ...newMission, quiz_correct: e.target.value })}
                    placeholder="Richtige Antwort..."
                  />
                </div>
              )}

              <Button onClick={handleCreateMission} className="w-full">
                <Plus className="w-4 h-4 mr-2" />
                Mission erstellen
              </Button>
            </div>

            {/* Existing Missions */}
            <div className="space-y-3">
              {missions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Target className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>Noch keine Missionen</p>
                </div>
              ) : (
                missions.map((mission) => (
                  <div key={mission.id} className="p-4 border rounded-lg bg-card">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-2xl">{getMissionIcon(mission.mission_type)}</span>
                          <h5 className="font-semibold">{mission.title}</h5>
                          <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full font-medium">
                            +{mission.points} Punkte
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground mb-3">{mission.description}</p>
                        
                        {/* Submissions */}
                        {mission.submissions && mission.submissions.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-xs font-medium text-muted-foreground">Submissions:</p>
                            {mission.submissions.map((sub, idx) => (
                              <div key={idx} className="flex items-center justify-between p-2 bg-secondary/20 rounded text-sm">
                                <div>
                                  <span className="font-medium">{sub.team_name}</span>
                                  {sub.photo_url && (
                                    <a href={`${BACKEND_URL}${sub.photo_url}`} target="_blank" rel="noopener noreferrer" className="ml-2 text-primary underline text-xs">
                                      📸 Foto ansehen
                                    </a>
                                  )}
                                </div>
                                {sub.status === "pending" && (
                                  <Button
                                    size="sm"
                                    onClick={() => handleApproveSubmission(mission.id, sub.team_id)}
                                  >
                                    Freigeben
                                  </Button>
                                )}
                                {sub.status === "approved" && <span className="text-green-600 text-xs">✓ Freigegeben</span>}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          {/* Teams Tab */}
          <TabsContent value="teams" className="space-y-4">
            <div className="p-4 bg-secondary/20 rounded-lg space-y-3">
              <h4 className="font-medium">👥 Team manuell erstellen</h4>
              <div>
                <Label>Team-Name</Label>
                <Input
                  value={newTeamName}
                  onChange={(e) => setNewTeamName(e.target.value)}
                  placeholder="z.B. Die Unschlagbaren"
                />
              </div>
              <div>
                <Label>Mitglieder (kommagetrennt)</Label>
                <Input
                  value={newTeamMembers}
                  onChange={(e) => setNewTeamMembers(e.target.value)}
                  placeholder="Max, Lisa, Tom"
                />
              </div>
              <Button onClick={handleCreateTeam} className="w-full">
                <Plus className="w-4 h-4 mr-2" />
                Team erstellen
              </Button>
            </div>

            <div className="p-4 bg-primary/10 rounded-lg space-y-3">
              <h4 className="font-medium">🤖 Teams automatisch generieren</h4>
              <p className="text-sm text-muted-foreground">Verteilt alle Gäste gleichmäßig auf Teams</p>
              <div>
                <Label>Anzahl Teams</Label>
                <Input
                  type="number"
                  value={autoTeamCount}
                  onChange={(e) => setAutoTeamCount(parseInt(e.target.value) || 3)}
                  min={2}
                  max={10}
                />
              </div>
              <Button onClick={handleAutoGenerateTeams} variant="outline" className="w-full">
                Teams generieren
              </Button>
            </div>

            {/* Existing Teams */}
            <div className="space-y-3">
              {teams.map((team) => (
                <div key={team.id} className="p-4 border rounded-lg bg-card">
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="font-semibold flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      {team.name}
                    </h5>
                    <span className="text-sm font-medium text-primary">{team.points} Punkte</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {team.members.join(", ")}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {team.completed_missions?.length || 0} Missionen erledigt
                  </p>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Leaderboard Tab */}
          <TabsContent value="leaderboard" className="space-y-3">
            {leaderboard.map((team) => (
              <div
                key={team.id}
                className={`p-4 rounded-lg border-2 ${
                  team.rank === 1 ? 'bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border-yellow-500' :
                  team.rank === 2 ? 'bg-gradient-to-r from-gray-400/20 to-gray-500/20 border-gray-400' :
                  team.rank === 3 ? 'bg-gradient-to-r from-amber-600/20 to-amber-700/20 border-amber-600' :
                  'bg-card border-border'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className="text-3xl font-bold w-12 text-center">
                    {team.rank === 1 ? '🥇' : team.rank === 2 ? '🥈' : team.rank === 3 ? '🥉' : `#${team.rank}`}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-lg">{team.name}</p>
                    <p className="text-sm text-muted-foreground">{team.members.join(", ")}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-primary">{team.points}</p>
                    <p className="text-xs text-muted-foreground">Punkte</p>
                  </div>
                </div>
              </div>
            ))}
          </TabsContent>
        </Tabs>
      ) : (
        // Guest View
        <div className="space-y-6">
          {/* Team Selection */}
          <div className="p-4 bg-secondary/20 rounded-lg">
            <Label className="mb-2 block">Dein Team</Label>
            <Select value={selectedTeam} onValueChange={setSelectedTeam}>
              <SelectTrigger>
                <SelectValue placeholder="Team auswählen..." />
              </SelectTrigger>
              <SelectContent>
                {teams.map((team) => (
                  <SelectItem key={team.id} value={team.id}>
                    {team.name} ({team.members.join(", ")})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Missions */}
          <div className="space-y-3">
            {missions.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Target className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p>Noch keine Missionen</p>
              </div>
            ) : (
              missions.map((mission) => {
                const status = getMissionStatus(mission, selectedTeam);
                
                return (
                  <div key={mission.id} className="p-4 border rounded-lg bg-card">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-2xl">{getMissionIcon(mission.mission_type)}</span>
                          <h5 className="font-semibold">{mission.title}</h5>
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">{mission.description}</p>
                        <div className="flex items-center gap-2 text-xs">
                          <span className={`px-2 py-1 rounded-full text-white ${status.color}`}>
                            {status.label}
                          </span>
                          <span className="text-muted-foreground">+{mission.points} Punkte</span>
                        </div>
                      </div>
                    </div>

                    {selectedTeam && status.status === "todo" && (
                      <div className="mt-3">
                        {mission.mission_type === "photo" ? (
                          <div>
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              id={`photo-${mission.id}`}
                              onChange={(e) => handleSubmitPhoto(mission.id, e.target.files[0])}
                            />
                            <label htmlFor={`photo-${mission.id}`} className="block">
                              <Button variant="outline" className="w-full" asChild>
                                <span>
                                  <Upload className="w-4 h-4 mr-2" />
                                  Foto hochladen
                                </span>
                              </Button>
                            </label>
                          </div>
                        ) : (
                          <Button
                            onClick={() => handleSubmitMission(mission.id, mission.mission_type)}
                            className="w-full"
                            disabled={!selectedTeam}
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Als erledigt markieren
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Team Leaderboard */}
          {leaderboard.length > 0 && (
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Trophy className="w-5 h-5 text-primary" />
                Team-Leaderboard
              </h4>
              <div className="space-y-2">
                {leaderboard.slice(0, 5).map((team) => (
                  <div key={team.id} className="flex items-center justify-between p-3 bg-card border rounded">
                    <div className="flex items-center gap-3">
                      <span className="text-lg font-bold w-8">
                        {team.rank <= 3 ? ['🥇', '🥈', '🥉'][team.rank - 1] : `#${team.rank}`}
                      </span>
                      <span className="font-medium">{team.name}</span>
                    </div>
                    <span className="font-bold text-primary">{team.points}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
